import React from 'react';
import { withApollo } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import queries from '../../../queries';

// Visual editor for the HTML shown in an Agenda 2.0 popup. It edits the stored HTML directly (links, lists,
// colours and images such as logos are kept as is), unlike react-rte which would rewrite it in its own format.
// The "HTML" switch gives access to the raw markup.

const COLORS = [
  ['Vert Euroseeds', 'rgb(166,200,42)'],
  ['Mauve', 'rgb(101,68,85)'],
  ['Noir', 'rgb(0,0,0)'],
  ['Blanc', 'rgb(255,255,255)'],
];

// Text pasted from Word / a web page carries its own font, size and spacing inline, which makes one popup
// look different from the others. Keep the meaning (bold, italic, underline, colour, links, lists) and drop the rest.
const DROPPED_STYLES = /^(font-family|font-size|line-height|background|margin|padding|width|height|text-indent|letter-spacing|mso-|-webkit-|-moz-)/;
function cleanPastedHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script,style,meta,link,title,xml').forEach((n) => n.remove());
  doc.querySelectorAll('*').forEach((n) => {
    if (n.tagName === 'FONT') {
      const span = doc.createElement('span');
      while (n.firstChild) span.appendChild(n.firstChild);
      n.replaceWith(span);
      n = span;
    }
    n.removeAttribute('class');
    n.removeAttribute('id');
    n.removeAttribute('lang');
    if (n.style) {
      Array.from(n.style).filter((prop) => DROPPED_STYLES.test(prop)).forEach((prop) => n.style.removeProperty(prop));
      if (!n.getAttribute('style')) n.removeAttribute('style');
    }
  });
  return doc.body.innerHTML;
}

const SIZES = [
  ['Très petit', '0.75em'],
  ['Petit', '0.9em'],
  ['Normal', '1em'],
  ['Grand', '1.25em'],
  ['Très grand', '1.6em'],
  ['Énorme', '2em'],
];

// Sizes typed in pixels are stored in rem so they scale with the screen: the root font size of a screen is
// default_font_ratio x screen width, so the pixels typed here are the ones obtained on a REF_WIDTH wide screen.
const REF_WIDTH = 1080;
const DEFAULT_FONT_RATIO = 0.025;

const barStyle = { display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 6 };
const activeStyle = { background: 'var(--tsk-teal-dim, #2a6f6f)', borderColor: 'var(--tsk-text)', boxShadow: 'inset 0 0 0 1px var(--tsk-text)' };
const buttonStyle = { minWidth: 30, height: 28, padding: '0 8px', cursor: 'pointer', borderRadius: 4, border: '1px solid var(--tsk-border)', background: 'var(--tsk-bg-raised)', color: 'var(--tsk-text)' };

class HtmlDescriptionEditor extends React.Component {

  state = { html: false, active: {}, fonts: [], font: '', size: '', px: '', ratio: DEFAULT_FONT_RATIO };
  editor = React.createRef();
  lastEmitted = null;

  componentDidMount() {
    this.syncFromValue();
    document.addEventListener('selectionchange', this.refreshActive);
    const { client, match } = this.props;
    const id = match && match.params && match.params.projectId;
    if (client && id) {
      client.query({ query: queries.projects.getWithChildren, variables: { id } })
        .then((res) => this.setState({ fonts: ((res.data.project || {}).fonts || []).map((f) => f.name), ratio: (res.data.project || {}).default_font_ratio || DEFAULT_FONT_RATIO }))
        .catch(() => {});
    }
  }

  componentWillUnmount() {
    document.removeEventListener('selectionchange', this.refreshActive);
  }

  // Current size shown in the px field (empty for sizes given in em / keywords).
  sizeToPx(size) {
    if (/rem$/.test(size)) return String(Math.round(parseFloat(size) * this.state.ratio * REF_WIDTH));
    if (/px$/.test(size)) return String(parseFloat(size)); // sizes set before sizes became responsive
    return '';
  }

  // Highlight the buttons whose effect applies where the caret / selection is.
  refreshActive = () => {
    const el = this.editor.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.anchorNode || !el.contains(sel.anchorNode)) return;
    if (sel.rangeCount) this.savedRange = sel.getRangeAt(0).cloneRange();
    const state = (c) => { try { return document.queryCommandState(c); } catch (e) { return false; } };
    const active = { bold: state('bold'), italic: state('italic'), underline: state('underline'), list: state('insertUnorderedList') };
    let node = sel.anchorNode.nodeType === 3 ? sel.anchorNode.parentNode : sel.anchorNode;
    let font = '';
    let size = '';
    let link = false;
    while (node && node !== el) {
      if (node.nodeName === 'A') link = true;
      if (!size && node.style && node.style.fontSize) size = node.style.fontSize;
      if (!font && node.style && node.style.fontFamily) font = node.style.fontFamily.replace(/["']/g, '');
      node = node.parentNode;
    }
    active.link = link;
    const prev = this.state.active;
    if (font !== this.state.font || size !== this.state.size || Object.keys(active).some((k) => active[k] !== prev[k])) this.setState({ active, font, size, px: this.sizeToPx(size) });
  };

  componentDidUpdate() {
    this.syncFromValue();
  }

  // Only rewrite the editable zone when the value changed from outside, otherwise the caret would jump.
  syncFromValue() {
    const el = this.editor.current;
    const value = this.props.value || '';
    if (el && value !== this.lastEmitted && el.innerHTML !== value) {
      el.innerHTML = value;
    }
    this.lastEmitted = value;
  }

  emit = () => {
    const el = this.editor.current;
    if (!el) return;
    this.lastEmitted = el.innerHTML;
    this.props.onChange(el.innerHTML);
  };

  run(command, arg) {
    const el = this.editor.current;
    if (!el) return;
    el.focus();
    if (this.savedRange) {
      const sel = window.getSelection();
      if (!sel.rangeCount || !el.contains(sel.anchorNode)) { sel.removeAllRanges(); sel.addRange(this.savedRange); }
    }
    document.execCommand('styleWithCSS', false, true);
    document.execCommand(command, false, arg);
    this.emit();
  }

  // execCommand only knows 7 browser sizes: apply the largest one, then turn it into the chosen size (em).
  setFontSize(size) {
    const el = this.editor.current;
    if (!el || !size) return;
    el.focus();
    if (this.savedRange) {
      const sel = window.getSelection();
      if (!sel.rangeCount || !el.contains(sel.anchorNode)) { sel.removeAllRanges(); sel.addRange(this.savedRange); }
    }
    document.execCommand('styleWithCSS', false, false);
    document.execCommand('fontSize', false, '7');
    el.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement('span');
      span.style.fontSize = size;
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);
    });
    this.emit();
  }

  applyPx() {
    const n = parseFloat(this.state.px);
    if (!n || n < 6 || n > 200) return;
    if (this.sizeToPx(this.state.size) === String(n)) return; // nothing to do (the field was only focused)
    this.setFontSize(`${(n / (this.state.ratio * REF_WIDTH)).toFixed(4)}rem`);
  }

  paste = (e) => {
    const data = e.clipboardData;
    const html = data && data.getData('text/html');
    if (!html) return; // plain text: let the browser insert it
    e.preventDefault();
    document.execCommand('insertHTML', false, cleanPastedHtml(html));
    this.emit();
  };

  link() {
    const url = window.prompt('Adresse du lien (https://…)');
    if (url) this.run('createLink', url);
  }

  button(label, title, onClick, isActive) {
    return (
      <button type="button" key={title} title={title} aria-pressed={!!isActive} style={{ ...buttonStyle, ...(isActive ? activeStyle : null) }} onMouseDown={(e) => { e.preventDefault(); onClick(); }}>
        {label}
      </button>
    );
  }

  render() {
    const { value, onChange } = this.props;
    const { html, active, fonts, font, size } = this.state;

    return (
      <div>
        <div style={barStyle}>
          {!html && this.button(<b>G</b>, 'Gras', () => this.run('bold'), active.bold)}
          {!html && this.button(<i>I</i>, 'Italique', () => this.run('italic'), active.italic)}
          {!html && this.button(<u>S</u>, 'Souligné', () => this.run('underline'), active.underline)}
          {!html && this.button('• Liste', 'Liste à puces', () => this.run('insertUnorderedList'), active.list)}
          {!html && this.button('Lien', 'Ajouter un lien', () => this.link(), active.link)}
          {!html && this.button('Sans lien', 'Retirer le lien', () => this.run('unlink'))}
          {!html && (
            <select
              title="Typographie"
              value={fonts.includes(font) ? font : ''}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => { if (e.target.value) this.run('fontName', e.target.value); }}
              style={{ height: 28, width: 170, padding: '0 6px', fontSize: 13, boxSizing: 'border-box' }}
            >
              <option value="">Typographie…</option>
              {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          )}
          {!html && (
            <select
              title="Taille du texte"
              value={SIZES.some(([, v]) => v === size) ? size : ''}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => { this.setFontSize(e.target.value); }}
              style={{ height: 28, width: 110, padding: '0 6px', fontSize: 13, boxSizing: 'border-box' }}
            >
              <option value="">Taille…</option>
              {SIZES.map(([label, value]) => <option key={value} value={value}>{label}</option>)}
            </select>
          )}
          {!html && (
            <span title="Taille en pixels sur un écran de 1080 px de large : elle s'adapte automatiquement aux autres écrans (Entrée pour appliquer)" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <input
                type="number"
                min="4"
                max="200"
                step="1"
                placeholder="taille"
                value={this.state.px}
                onChange={(e) => this.setState({ px: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.applyPx(); } }}
                onBlur={() => this.applyPx()}
                style={{ height: 28, width: 64, padding: '0 6px', fontSize: 13, boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: 12, opacity: 0.7 }}>px</span>
            </span>
          )}
          {!html && COLORS.map(([name, color]) => this.button(
            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: color, border: '1px solid rgba(128,128,128,0.6)', verticalAlign: 'middle' }} />,
            `Couleur : ${name}`, () => this.run('foreColor', color)))}
          {!html && this.button('Effacer le style', 'Retirer la mise en forme de la sélection', () => this.run('removeFormat'))}
          <span style={{ flex: 1 }} />
          {this.button(html ? 'Éditeur visuel' : 'HTML', 'Basculer entre éditeur visuel et code HTML', () => this.setState({ html: !html }))}
        </div>
        {html
          ? <textarea rows={10} value={value || ''} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', fontFamily: 'monospace' }} />
          : (
            <>
            <style>{'.tsk-html-editable ul{display:block !important;list-style:disc !important;padding-left:1.4em !important;margin:0.4em 0 !important}.tsk-html-editable li{display:list-item !important;list-style:inherit !important}.tsk-html-editable a{color:rgb(101,68,85);text-decoration:underline}'}</style>
            <div
              className="tsk-html-editable"
              ref={this.editor}
              contentEditable
              suppressContentEditableWarning
              onInput={this.emit}
              onBlur={this.emit}
              onPaste={this.paste}
              onKeyUp={this.refreshActive}
              onMouseUp={this.refreshActive}
              style={{ minHeight: 140, maxHeight: 360, overflowY: 'auto', padding: '8px 10px', borderRadius: 4, border: '1px solid var(--tsk-border)', background: '#fff', color: '#222', lineHeight: 1.4, outline: 'none' }}
            />
            </>
          )}
      </div>
    );
  }
}

export default withRouter(withApollo(HtmlDescriptionEditor));
